import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { PostOrgPaymentsCreditCardChargeApiArg } from '@/__generated/es-api'
import { selectToken } from '@/app/module/auth/store/selectors'
import { createNotification } from '@/app/module/notifications/store/actions'
import {
  ERROR_CHARGE_CARD_SERVER,
  ERROR_CHARGE_UNDER_REVIEW,
  SUCCESS_CHARGE_CARD_SERVER,
  UNDER_REVIEW_CHARGE_CARD_SERVER,
} from '@/app/module/payments/definitions'
import { getUser } from '@/app/module/user/store/actions'
import { selectOrgId } from '@/app/module/user/store/selectors'
import { esApi } from '@/app/store/api'
import { useAppDispatch } from '@/app/store/redux-hooks'

export const useChargeCreditCard = () => {
  const dispatch = useAppDispatch()
  const orgId = useSelector(selectOrgId)
  const token = useSelector(selectToken)

  const [mutate, res] = esApi.endpoints.postOrgPaymentsCreditCardCharge.useMutation()

  const chargeCard = useCallback(
    async (args: { ccid: PostOrgPaymentsCreditCardChargeApiArg['ccid']; amountUSD: number }) => {
      const amountUSDcent = Math.round(args.amountUSD * 100)
      try {
        const { status } = await mutate({
          orgId: orgId.toString(),
          ccid: args.ccid,
          body: { amountUSDcent },
        }).unwrap()

        const amountStr = args.amountUSD.toFixed(2)
        const messageMap: Record<string, string> = {
          success: SUCCESS_CHARGE_CARD_SERVER.replace('%AMOUNT%', amountStr),
          'on hold': UNDER_REVIEW_CHARGE_CARD_SERVER,
          declined: ERROR_CHARGE_CARD_SERVER,
        }

        dispatch(
          createNotification({
            'charge-card': {
              type: 'success',
              message: messageMap[status] || SUCCESS_CHARGE_CARD_SERVER.replace('%AMOUNT%', amountStr),
            },
          }),
        )

        dispatch(getUser({ token }))
        return true
      } catch (err: any) {
        const message = err?.status === 423 ? ERROR_CHARGE_UNDER_REVIEW : ERROR_CHARGE_CARD_SERVER
        dispatch(
          createNotification({
            'charge-card': {
              type: 'error',
              message,
            },
          }),
        )
        return false
      }
    },
    [orgId, token, dispatch, mutate],
  )

  return [chargeCard, res] as const
}
